import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import AuthLayout, { GoogleButton, Divider, Field, SubmitBtn } from "./AuthLayout";
import { freeTrialBlurb } from "@/lib/planCopy";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return String(detail);
}

export default function Signup() {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const prefilledEmail = (sp.get("email") || "").trim();
  const [form, setForm] = useState({ email: prefilledEmail, password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const handle = async (e) => {
    e.preventDefault();
    setErr(null);
    if (form.password !== form.confirm) {
      setErr("Passwords don't match.");
      return;
    }
    if (form.password.length < 8) {
      setErr("Password must be at least 8 characters.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/signup", { email: form.email, password: form.password });
      toast.success("Check your email for a 6-digit code.");
      nav(`/verify-otp?email=${encodeURIComponent(form.email.toLowerCase().trim())}`);
    } catch (e) {
      setErr(formatError(e?.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout
      title="Create your account"
      subtitle={`${freeTrialBlurb()} No credit card needed.`}
      footer={
        <>
          Already have an account?{" "}
          <Link to="/login" className="font-semibold text-lm-primary hover:text-lm-primaryHover underline underline-offset-4">
            Sign in
          </Link>
        </>
      }
    >
      <GoogleButton label="Sign up with Google" />
      <Divider />
      <form onSubmit={handle} data-testid="signup-form">
        <Field label="Email" id="email" name="email" type="email" required autoComplete="email"
          placeholder="you@company.com"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
          data-testid="signup-email" />
        <Field label="Password" id="password" name="password" type="password" required autoComplete="new-password"
          placeholder="At least 8 characters" hint="8+ chars"
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
          data-testid="signup-password" />
        <Field label="Confirm password" id="confirm" name="confirm" type="password" required autoComplete="new-password"
          placeholder="Re-enter password"
          value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })}
          data-testid="signup-confirm" />
        {err && <div className="mb-3 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2">{err}</div>}
        <SubmitBtn loading={busy} data-testid="signup-submit">Create account</SubmitBtn>
      </form>
      <p className="mt-5 text-xs text-lm-text3 leading-relaxed text-center">
        By continuing you agree to our{" "}
        <Link to="/terms" className="underline underline-offset-4">Terms</Link> and{" "}
        <Link to="/privacy" className="underline underline-offset-4">Privacy Policy</Link>.
      </p>
    </AuthLayout>
  );
}
